import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Reveal, Spinner } from "@/components/public/Primitives";
import { GALLERY_CATEGORIES } from "@/data/constants";
import { useLanguage } from "@/context/LanguageContext";
import { getGallery } from "@/lib/api";

export default function Gallery() {
  const { data: images = [], isLoading } = useQuery({ queryKey: ["gallery"], queryFn: getGallery });
  const [category, setCategory] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const { tr } = useLanguage();

  const filtered = category === "All" ? images : images.filter((g) => g.category === category);

  return (
    <div data-testid="gallery-page">
      <PageSeo
        slug="gallery"
        title="Gallery | Girish Kumar Naidu"
        description="A photo gallery of Girish Kumar Naidu's social work, public meetings, trust activities, health camps, and community events."
        keywords="Girish Kumar Naidu gallery, social work photos, community events Bengaluru"
        schemas={[
          breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Gallery" }]),
          ...images.slice(0, 12).map((g) => ({
            "@context": "https://schema.org", "@type": "ImageObject", contentUrl: g.image, name: g.title, caption: g.altText,
          })),
        ]}
      />
      <PageHeader
        overline={tr("page.gallery.overline")}
        title={tr("page.gallery.title")}
        subtitle={tr("page.gallery.subtitle")}
        crumbs={[{ name: "Gallery" }]}
      />

      <section className="bg-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex flex-wrap gap-2 mb-12" data-testid="gallery-filters">
            {GALLERY_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                  category === c ? "bg-gold text-white" : "bg-paper border border-gray-200 text-gray-600 hover:border-gold hover:text-gold"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {isLoading ? (
            <Spinner />
          ) : (
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
              {filtered.map((g, i) => (
                <Reveal key={g.id} delay={(i % 4) * 0.05}>
                  <button
                    onClick={() => setLightbox(g)}
                    className="group relative w-full rounded-2xl overflow-hidden block border border-gray-200"
                    data-testid={`gallery-item-${i}`}
                  >
                    <img src={g.image} alt={g.altText || g.title} className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <div className="text-left">
                        <p className="text-white text-sm font-bold">{g.title}</p>
                        <p className="font-mono text-[10px] uppercase tracking-wider text-gold">{g.category}</p>
                      </div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-navy/95 flex items-center justify-center p-5"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute top-6 right-6 text-white h-11 w-11 rounded-full glass-dark flex items-center justify-center">
              <X className="h-5 w-5" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }} animate={{ scale: 1 }}
              src={lightbox.image} alt={lightbox.altText}
              className="max-h-[85vh] max-w-full rounded-2xl object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
